import { useEffect, useState, useCallback, useMemo } from "react";
import { MuiMaterials } from '../@index.js';
import * as global from '../data/global';
import { createApiEndpoint } from '../index.js';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import { outlinedActionButtonSx } from './windowChrome/index.js';

const readCarbonDioxideBins = () => {
    const bins = global.gw.selectedSite?.data?.bins;
    if (!Array.isArray(bins)) return [];
    return bins.map((b) => ({
        binName: b?.name ?? "",
        binId: b?.binId ?? 0,
        cableList: (Array.isArray(b?.cableList) ? b.cableList : []).map((c) => ({
            binId: b?.binId ?? 0,
            cableId: c?.id,
            cableName: c?.name ?? "",
            sensorList: (Array.isArray(c?.sensorList) ? c.sensorList : []).map((s) => ({
                binId: b?.binId ?? 0,
                cableId: s?.cableId,
                sensorId: s?.id,
                sensorName: s?.name ?? "",
            })),
        })),
    }));
};

const CarbonDioxideSettings = ({ updateToggle, setUpdateToggle }) => {
    const [binIds, setBinIds] = useState(readCarbonDioxideBins);
    const [deviceIds, setDeviceIds] = useState(null);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(null);
    const [selectedBinId, setSelectedBinId] = useState([0]);
    const [weatherStationReadingSettings, setWeatherStationReadingSettings] = useState([{
        DeviceId: "All",
        HighLimit: null,
        IsEmailOnHighLimit: false,
        WarningLimit: null,
        IsEmailOnHighWarning: false,
    }]);
    const [selectedDeviceId, setSelectedDeviceId] = useState("All");
    const [devices, setDevices] = useState(null);
    const [forceUpdate, setForceUpdate] = useState(0);

    // Helper function to get theme-aware colors (reads directly from global.gw.theme)
    const getThemeColor = useCallback((lightColor, darkColor) => {
        if (global.gw.theme === 'light') {
            return lightColor;
        }
        return darkColor;
    }, []);

    // Listen for theme changes - only trigger re-render when theme actually changes
    useEffect(() => {
        const handleThemeChange = () => {
            setForceUpdate(prev => prev + 1);
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);

    const theme = useMemo(() => {
        const isLight = global.gw.theme === 'light';
        return MuiMaterials.createTheme({
    components: {
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: isLight ? '#000000' : '#f4cc8e',
                    fontSize: "18px",
                    borderColor: isLight ? '#666666' : 'rgb(244, 204, 142)',
                    ...(isLight && {
                        '&:not(.MuiInputLabel-shrink)': {
                            top: '50%',
                            transform: 'translateY(-50%)',
                            left: '14px',
                        },
                    }),
                },
                shrink: {
                    top: isLight ? '8px' : undefined,
                },
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontSize: "18px",
                    background: isLight ? '#e0e0e0' : '#2d2d2d',
                    color: isLight ? 'rgb(51, 51, 51)' : 'rgb(244, 204, 142)',
                }
            }
        },
        MuiIconButton: {
            styleOverrides: {
                sizeMedium: {
                    color: isLight ? 'rgb(51, 51, 51)' : 'rgb(244, 204, 142)'
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    color: isLight ? '#333333' : '#f4cc8e',
                    "& .MuiOutlinedInput-notchedOutline": {
                        border: isLight ? '0.5px solid #cccccc' : '0.5px solid #f4cc8e4d',
                    },
                    "&.Mui-focused": {
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: isLight ? '0.5px solid #666666' : '0.5px solid #f4cc8e',
                        },
                    },
                    "&:hover": {
                        "& .MuiOutlinedInput-notchedOutline": {
                            border: isLight ? '0.5px solid #666666' : '0.5px solid #f4cc8e',
                        },
                    },
                }
            }
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    ".MuiInputLabel-root.Mui-focused": {
                        color: isLight ? '#000000' : '#f4cc8e',
                    },
                },
            }
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    border: isLight ? '0.5px solid #cccccc' : '0.5px solid #f4cc8e4d',
                    ".MuiInputLabel-root.Mui-focused": {
                        color: isLight ? '#000000' : '#f4cc8e',
                    },
                    ".MuiSvgIcon-root": {
                        color: isLight ? '#333333' : '#f4cc8e',
                    },
                },
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: isLight ? '#333333' : '#f4cc8e',
                },
            }
        },
    },
    });
    }, [forceUpdate]);

    useEffect(() => {
        if (!isSettingsModalOpen) {
            const fetchSiteWeatherStationTablesTask = () => {
                return createApiEndpoint().fetchSiteWeatherStationTables("direct");
            };

            fetchSiteWeatherStationTablesTask().then(async (response) => {
                if (response && response.data && response.data.getSiteWeatherStationTables) {
                    let arrayBuffer = global.base64StringToArrayBuffer(response.data.getSiteWeatherStationTables);
                    let decompressed = await global.decompress(arrayBuffer, "gzip");
                    let weatherStationTables = JSON.parse(decompressed);

                    if (!weatherStationTables?.WeatherStationReadingSetting) {
                        weatherStationTables.WeatherStationReadingSetting = [{
                            DeviceId: "All",
                            HighLimit: null,
                            IsEmailOnHighLimit: false,
                            WarningLimit: null,
                            IsEmailOnHighWarning: false,
                        }];
                    }
                    else {
                        weatherStationTables.WeatherStationReadingSetting.splice(0, 0, {
                            DeviceId: "All",
                            HighLimit: null,
                            IsEmailOnHighLimit: false,
                            WarningLimit: null,
                            IsEmailOnHighWarning: false,
                        })
                    }

                    setWeatherStationReadingSettings(weatherStationTables?.WeatherStationReadingSetting);

                    let weatherStationDevices = weatherStationTables?.WeatherStationDevice;
                    if (!weatherStationDevices || weatherStationDevices === "") {
                        weatherStationDevices = [{
                            BinId: 0,
                            DeviceId: "All",
                        }];
                    }
                    else if (weatherStationDevices.length > 0 && weatherStationDevices[0].DeviceId !== "All") {
                        weatherStationDevices.splice(0, 0, {
                            BinId: 0,
                            DeviceId: "All",
                        })
                    }

                    let tempDeviceIds = weatherStationDevices.map(x => { return { DeviceId: x.DeviceId, BinId: x.BinId } });
                    setDeviceIds(tempDeviceIds);

                    let tempSelectedBinId = selectedBinId;
                    tempSelectedBinId[0] = selectedBinId[0];
                    setSelectedBinId(tempSelectedBinId);

                    renderDevices(tempSelectedBinId[0], tempDeviceIds);
                }
                else {

                }
            }).catch(function (error) {

            });
        }
    }, [isSettingsModalOpen]);

    const handleBinChange = (event) => {
        let tempSelectedBinId = selectedBinId;
        tempSelectedBinId[0] = event.target.value;
        setSelectedBinId([event.target.value]);

        renderDevices(tempSelectedBinId[0]);

        if (deviceIds && deviceIds.length > 0) {
            setSelectedDeviceId(deviceIds[0].DeviceId);
        }
    };

    const renderBins = () => {;
        if (binIds && binIds.length > 0 && binIds[0].binName != "All") {
            binIds.splice(0, 0, {
                binId: 0,
                binName: "All",
            });
        }
        return (binIds.map((b, i) => (
            <MuiMaterials.MenuItem value={b.binId}>{b.binName}</MuiMaterials.MenuItem>
        )));
    };

    const renderDevices = (binId, deviceList) => {
        let binDevices = deviceList ? deviceList : deviceIds;
        if (binDevices) {
            if (binId != 0) {
                binDevices = binDevices.filter(d => d.BinId === binId);
            }
            if (binDevices && binDevices.length > 0 && binDevices[0].DeviceId != "All") {
                binDevices.splice(0, 0, {
                    BinId: binId,
                    DeviceId: "All",
                });
            }

            setDevices(binDevices.map((c, i) => (
                <MuiMaterials.MenuItem value={c.DeviceId}>{c.DeviceId}</MuiMaterials.MenuItem>
            )));
        }
    };

    const handleDeviceIdChange = (event) => {
        setSelectedDeviceId(event.target.value);
    };

    const renderSettingsModal = () => {
        return (   
            <MuiMaterials.Modal
                key={`co2-settings-modal-${global.gw.theme}-${forceUpdate}`}
                className="settingsModal hide-scrollbar"
                open={isSettingsModalOpen}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                sx={{
                    backgroundColor: getThemeColor("rgba(255, 255, 255, 0.45)", "rgba(0, 0, 0, 0.45)"),
                }}
            >
                <MuiMaterials.Box
                    className="settingsModalContent hide-scrollbar"
                    sx={{
                        color: getThemeColor('#333333', '#f4cc8e'),
                        display: "flex",
                        flexDirection: "row",
                        height: "100%",
                        width: "100%",
                        justifyContent: "center",
                        alignContent: "start",
                        flexWrap: "wrap",
                        overflowY: "auto",
                        maxHeight: "calc(100vh - 5px)",
                        background: getThemeColor('#ffffff', '#2d2d2d'),
                    }}
                >
                    <MuiMaterials.Button
                        sx={{
                            color: getThemeColor('#333333', '#f4cc8e'),
                            margin: "auto",
                            maxWidth: "40px",
                            maxHeight: "40px",
                            minWidth: "40px",
                            minHeight: "40px",
                            position: "absolute",
                            right: "0",
                            top: "0",
                            '&:hover': {
                                backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                            },
                        }}
                        onClick={e => {
                            setIsSettingsModalOpen(false);
                        }}
                    >
                        <CloseIcon />
                    </MuiMaterials.Button>

                    <MuiMaterials.Box>
                        <MuiMaterials.InputLabel
                            sx={{
                                fontSize: "18px",
                                background: getThemeColor('#ffffff', '#2d2d2d'),
                                color: getThemeColor('#333333', '#f4cc8e'),
                                margin: "15px 0px",
                                textAlign: "center",
                                padding: "9px 14px",
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: getThemeColor('#cccccc', '#f4cc8e'),
                                    },
                                },
                            }}
                        >
                            Edit CO2 Setpoints
                        </MuiMaterials.InputLabel>
                        <MuiMaterials.Box
                            noValidate
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                flexWrap: "wrap",
                            }}
                        >
                            <MuiMaterials.ThemeProvider theme={theme} key={`co2-settings-theme-${global.gw.theme}-${forceUpdate}`}>
                                <MuiMaterials.TextField
                                    select
                                    value={selectedBinId[0]}
                                    label="Bin"
                                    variant="outlined"
                                    InputProps={{
                                        style: {
                                            fontSize: "18px",
                                            background: getThemeColor('#ffffff', '#2d2d2d'),
                                            color: getThemeColor('#333333', '#f4cc8e'),
                                        }
                                    }}
                                    sx={{
                                        fontSize: "18px",
                                        background: getThemeColor('#ffffff', '#2d2d2d'),
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                        margin: "5px 0px",
                                        textAlign: "center",
                                        '& .MuiInputLabel-root': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: getThemeColor('#cccccc', '#f4cc8e'),
                                            },
                                            '&:hover fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                        },
                                    }}
                                    onChange={async (e) => {
                                        handleBinChange(e);
                                    }}
                                >
                                    {renderBins()}
                                </MuiMaterials.TextField>
                                <MuiMaterials.TextField
                                    select
                                    value={selectedDeviceId}
                                    label="Device Id"
                                    variant="outlined"
                                    InputProps={{
                                        style: {
                                            fontSize: "18px",
                                            background: getThemeColor('#ffffff', '#2d2d2d'),
                                            color: getThemeColor('#333333', '#f4cc8e'),
                                        }
                                    }}
                                    sx={{
                                        fontSize: "18px",
                                        background: getThemeColor('#ffffff', '#2d2d2d'),
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                        margin: "5px 0px",
                                        textAlign: "center",
                                        '& .MuiInputLabel-root': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: getThemeColor('#cccccc', '#f4cc8e'),
                                            },
                                            '&:hover fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                        },
                                    }}
                                    onChange={async (e) => {
                                        handleDeviceIdChange(e);
                                    }}
                                >
                                    {devices}
                                </MuiMaterials.TextField>
                                <MuiMaterials.TextField
                                    value={weatherStationReadingSettings.filter(x => x.DeviceId == selectedDeviceId)[0]?.CriticalLimit ?? ""}
                                    label="Critcal Limit"
                                    variant="outlined"
                                    InputProps={{
                                        style: {
                                            fontSize: "18px",
                                            background: getThemeColor('#ffffff', '#2d2d2d'),
                                            color: getThemeColor('#333333', '#f4cc8e'),
                                        }
                                    }}
                                    sx={{
                                        fontSize: "18px",
                                        background: getThemeColor('#ffffff', '#2d2d2d'),
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                        margin: "5px 0px",
                                        textAlign: "center",
                                        '& .MuiInputLabel-root': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: getThemeColor('#cccccc', '#f4cc8e'),
                                            },
                                            '&:hover fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                        },
                                    }}
                                    onChange={async (e) => {
                                        let tempWeatherStationReadingSettings = weatherStationReadingSettings.filter(x => x.DeviceId == selectedDeviceId);
                                        if (tempWeatherStationReadingSettings.length > 0) {
                                            tempWeatherStationReadingSettings[0].CriticalLimit = e.target.value;
                                            setWeatherStationReadingSettings(tempWeatherStationReadingSettings);
                                        }

                                    }}
                                />
                                <div style={{ textAlign: "center" }}>
                                    <MuiMaterials.FormControlLabel
                                        sx={{
                                            color: getThemeColor('#333333', '#f4cc8e'),
                                            margin: "0px",
                                            padding: "10px 0px",
                                        }}
                                        control={<MuiMaterials.Checkbox
                                            checked={weatherStationReadingSettings.filter(x => x.DeviceId == selectedDeviceId)[0]?.IsEmailOnCriticalLimit ?? false}
                                            sx={{
                                                color: getThemeColor('#333333', '#f4cc8e'),
                                                '&.Mui-checked': {
                                                    color: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                padding: "0px 9px 0px 0px",
                                            }}
                                            onChange={e => {
                                                let tempWeatherStationReadingSettings = weatherStationReadingSettings.filter(x => x.DeviceId == selectedDeviceId);
                                                if (tempWeatherStationReadingSettings.length > 0) {
                                                    tempWeatherStationReadingSettings[0].IsEmailOnCriticalLimit = e.target.checked;
                                                    setWeatherStationReadingSettings(tempWeatherStationReadingSettings);
                                                }
                                            }}
                                        />}
                                        label={"Email On Critical Limit?"}
                                    //id={"checkAllBins"}
                                    />
                                </div>
                                <MuiMaterials.TextField
                                    value={weatherStationReadingSettings.filter(x => x.DeviceId == selectedDeviceId)[0]?.HighLimit ?? ""}
                                    label="High Risk Limit"
                                    variant="outlined"
                                    InputProps={{
                                        style: {
                                            fontSize: "18px",
                                            background: getThemeColor('#ffffff', '#2d2d2d'),
                                            color: getThemeColor('#333333', '#f4cc8e'),
                                        }
                                    }}
                                    sx={{
                                        fontSize: "18px",
                                        background: getThemeColor('#ffffff', '#2d2d2d'),
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                        margin: "5px 0px",
                                        textAlign: "center",
                                        '& .MuiInputLabel-root': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: getThemeColor('#cccccc', '#f4cc8e'),
                                            },
                                            '&:hover fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                        },
                                    }}
                                    onChange={async (e) => {
                                        let tempWeatherStationReadingSettings = weatherStationReadingSettings.filter(x => x.DeviceId == selectedDeviceId);
                                        if (tempWeatherStationReadingSettings.length > 0) {
                                            tempWeatherStationReadingSettings[0].HighLimit = e.target.value;
                                            setWeatherStationReadingSettings(tempWeatherStationReadingSettings);
                                        }

                                    }}
                                />
                                <div style={{ textAlign: "center" }}>
                                    <MuiMaterials.FormControlLabel
                                        sx={{
                                            color: getThemeColor('#333333', '#f4cc8e'),
                                            margin: "0px",
                                            padding: "10px 0px",
                                        }}
                                        control={<MuiMaterials.Checkbox
                                            checked={weatherStationReadingSettings.filter(x => x.DeviceId == selectedDeviceId)[0]?.IsEmailOnHighLimit ?? false}
                                            sx={{
                                                color: getThemeColor('#333333', '#f4cc8e'),
                                                '&.Mui-checked': {
                                                    color: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                padding: "0px 9px 0px 0px",
                                            }}
                                            onChange={e => {
                                                let tempWeatherStationReadingSettings = weatherStationReadingSettings.filter(x => x.DeviceId == selectedDeviceId);
                                                if (tempWeatherStationReadingSettings.length > 0) {
                                                    tempWeatherStationReadingSettings[0].IsEmailOnHighLimit = e.target.checked;
                                                    setWeatherStationReadingSettings(tempWeatherStationReadingSettings);
                                                }
                                            }}
                                        />}
                                        label={"Email On High Risk Limit?"}
                                    //id={"checkAllBins"}
                                    />
                                </div>
                                <MuiMaterials.TextField
                                    value={weatherStationReadingSettings.filter(x => x.DeviceId == selectedDeviceId)[0]?.ModerateLimit ?? ""}
                                    label="Moderate Risk Limit"
                                    variant="outlined"
                                    InputProps={{
                                        style: {
                                            fontSize: "18px",
                                            background: getThemeColor('#ffffff', '#2d2d2d'),
                                            color: getThemeColor('#333333', '#f4cc8e'),
                                        }
                                    }}
                                    sx={{
                                        fontSize: "18px",
                                        background: getThemeColor('#ffffff', '#2d2d2d'),
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                        margin: "5px 0px",
                                        textAlign: "center",
                                        '& .MuiInputLabel-root': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: getThemeColor('#cccccc', '#f4cc8e'),
                                            },
                                            '&:hover fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                        },
                                    }}
                                    onChange={async (e) => {
                                        let tempWeatherStationReadingSettings = weatherStationReadingSettings.filter(x => x.DeviceId == selectedDeviceId);
                                        if (tempWeatherStationReadingSettings.length > 0) {
                                            tempWeatherStationReadingSettings[0].ModerateLimit = e.target.value;
                                            setWeatherStationReadingSettings(tempWeatherStationReadingSettings);
                                        }

                                    }}
                                />
                                <div style={{ textAlign: "center" }}>
                                    <MuiMaterials.FormControlLabel
                                        sx={{
                                            color: getThemeColor('#333333', '#f4cc8e'),
                                            margin: "0px",
                                            padding: "10px 0px",
                                        }}
                                        control={<MuiMaterials.Checkbox
                                            checked={weatherStationReadingSettings.filter(x => x.DeviceId == selectedDeviceId)[0]?.IsEmailOnModerateLimit ?? false}
                                            sx={{
                                                color: getThemeColor('#333333', '#f4cc8e'),
                                                '&.Mui-checked': {
                                                    color: getThemeColor('#333333', '#f4cc8e'),
                                                },
                                                padding: "0px 9px 0px 0px",
                                            }}
                                            onChange={e => {
                                                let tempWeatherStationReadingSettings = weatherStationReadingSettings.filter(x => x.DeviceId == selectedDeviceId);
                                                if (tempWeatherStationReadingSettings.length > 0) {
                                                    tempWeatherStationReadingSettings[0].IsEmailOnModerateLimit = e.target.checked;
                                                    setWeatherStationReadingSettings(tempWeatherStationReadingSettings);
                                                }
                                            }}
                                        />}
                                        label={"Email On Moderate Risk Limit?"}
                                    //id={"checkAllBins"}
                                    />
                                </div>
                                <MuiMaterials.TextField
                                   value={weatherStationReadingSettings.filter(x => x.DeviceId == selectedDeviceId)[0]?.HighWarning ?? ""}
                                    label="Early Warning Limit"
                                    variant="outlined"
                                    InputProps={{
                                        style: {
                                            fontSize: "18px",
                                            background: getThemeColor('#ffffff', '#2d2d2d'),
                                            color: getThemeColor('#333333', '#f4cc8e'),
                                        }
                                    }}
                                    sx={{
                                        fontSize: "18px",
                                        background: getThemeColor('#ffffff', '#2d2d2d'),
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                        margin: "5px 0px",
                                        textAlign: "center",
                                        '& .MuiInputLabel-root': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiInputLabel-root.Mui-focused': {
                                            color: getThemeColor('#000000', '#f4cc8e'),
                                            top: getThemeColor('8px', '0'),
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: getThemeColor('#cccccc', '#f4cc8e'),
                                            },
                                            '&:hover fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: getThemeColor('#666666', '#f4cc8e'),
                                            },
                                        },
                                    }}
                                    onChange={async (e) => {
                                        let tempWeatherStationReadingSettings = weatherStationReadingSettings.filter(x => x.DeviceId == selectedDeviceId);
                                        if (tempWeatherStationReadingSettings.length > 0) {
                                            tempWeatherStationReadingSettings[0].HighWarning = e.target.value;
                                            setWeatherStationReadingSettings(tempWeatherStationReadingSettings);
                                        }
                                    }}
                                    //{...(errors.last && { error: true, helperText: errors.last })}
                                />
                            </MuiMaterials.ThemeProvider>
                            <div style={{ textAlign: "center" }}>
                                <MuiMaterials.FormControlLabel
                                    sx={{
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                        margin: "0px",
                                        padding: "10px 0px",
                                    }}
                                    control={<MuiMaterials.Checkbox
                                        checked={weatherStationReadingSettings.filter(x => x.DeviceId == selectedDeviceId)[0]?.IsEmailOnHighWarning ?? false}
                                        sx={{
                                            color: getThemeColor('#333333', '#f4cc8e'),
                                            '&.Mui-checked': {
                                                color: getThemeColor('#333333', '#f4cc8e'),
                                            },
                                            padding: "0px 9px 0px 0px",
                                        }}
                                        onChange={e => {
                                            let tempWeatherStationReadingSettings = weatherStationReadingSettings.filter(x => x.DeviceId == selectedDeviceId);
                                            if (tempWeatherStationReadingSettings.length > 0) {
                                                tempWeatherStationReadingSettings[0].IsEmailOnHighWarning = e.target.checked;
                                                setWeatherStationReadingSettings(tempWeatherStationReadingSettings);
                                            }
                                        }}
                                    />}
                                    label={"Email On Early Warning Limit?"}
                                //id={"checkAllBins"}
                                />
                            </div>
                            <MuiMaterials.Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    marginTop: "5px",
                                    marginBottom: "15px",
                                }}
                            >
                                <MuiMaterials.Button
                                    key={`co2-settings-cancel-${global.gw.theme}-${forceUpdate}`}
                                    size="small"
                                    variant="outlined"
                                    sx={outlinedActionButtonSx()}
                                    onClick={async () => {
                                        //clearForm();
                                        setIsSettingsModalOpen(false);
                                    }}
                                >
                                    Cancel
                                </MuiMaterials.Button>
                                <MuiMaterials.Button
                                    key={`co2-settings-update-${global.gw.theme}-${forceUpdate}`}
                                    type="submit"
                                    size="small"
                                    variant="outlined"
                                    sx={outlinedActionButtonSx()}
                                    onClick={async () => {
                                        let weatherStationReadingSetting = weatherStationReadingSettings.filter(x => x.DeviceId === selectedDeviceId)[0];
                                        if (weatherStationReadingSetting) {
                                            const fetchUpsertWeatherStationReadingSettingTask = () => {
                                                weatherStationReadingSetting.BinId = selectedBinId[0] == "All" ? 0 : selectedBinId[0];;
                                                weatherStationReadingSetting.DeviceId = selectedDeviceId == "All" ? 0 : selectedDeviceId;
                                                return createApiEndpoint().fetchUpsertWeatherStationReadingSetting("direct", weatherStationReadingSetting);
                                            };

                                            await fetchUpsertWeatherStationReadingSettingTask().then(async (response) => {
                                                if (response && response.data && response.data.upsertWeatherStationReadingSetting) {
                                                    setUpdateToggle(!updateToggle);
                                                }
                                            }).catch(function (error) {

                                            });
                                        }
                                        setIsSettingsModalOpen(false);
                                    }}
                                >
                                    Update
                                </MuiMaterials.Button>
                            </MuiMaterials.Box>
                        </MuiMaterials.Box>
                    </MuiMaterials.Box>
                </MuiMaterials.Box>
            </MuiMaterials.Modal>
        );
    };

    return (
        <>
            <MuiMaterials.Button
                key={`co2-settings-button-${global.gw.theme}-${forceUpdate}`}
                sx={{
                    color: getThemeColor('#333333', '#f4cc8e'),
                    margin: "auto",
                    maxWidth: "40px",
                    maxHeight: "40px",
                    minWidth: "40px",
                    minHeight: "40px",
                    '&:hover': {
                        backgroundColor: getThemeColor('#f5f5f5', '#3d3d3d'),
                    },
                }}
                onClick={e => {
                    setIsSettingsModalOpen(true);
                }}
            >
                <SettingsIcon className="sei" />
            </MuiMaterials.Button>
            {renderSettingsModal()}
        </>
    );
}
export default CarbonDioxideSettings;